import {
  Button,
  Card,
  Descriptions,
  DescriptionsProps,
  Divider,
  Flex,
  Form,
  Grid,
  Image,
  Input,
  Rate,
} from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import defaultImage from "../../assets/images/content/empty_session.webp";
import FeedbackSubmitedIcon from "../../assets/images/content/feedback_submited.png";
import CalendarBlankFilled from "../../assets/images/icons/CalendarBlankFilled";
import ClockFilled from "../../assets/images/icons/ClockFilled";
import Copy from "../../assets/images/icons/Copy";
import { CompanionForm, CustomMessage } from "../../components";
import { H6, P1, P2, P3 } from "../../components/NormalFonts";
import { orderStatus } from "../../enumerations/orderStatus";
import { sessionType } from "../../enumerations/sessionType";
import {
  useGetPaymentMethod,
  useUpdateCompanion,
} from "../../hooks/Checkout/checkout.hooks";
import { PaymentResponse } from "../../hooks/Checkout/data";
import {
  CompanionType,
  OrderDetailResponse,
  SessionListType,
} from "../../hooks/Order/data";
import {
  useGetOrderDetail,
  useGetOrderFeedbackNew,
  useOrderFeedbackCreate,
} from "../../hooks/Order/order.hooks";
import useGetTimezone from "../../hooks/Timezone/timezone.hooks";
import { ContentWrapper } from "../../layouts/components";
import { moneyFormat } from "../../plugins/numberFormat";
import { copyText } from "../../plugins/utils";
import { orderType } from "../Record/enumerations/orderType";
import { paymentStatus } from "../Record/enumerations/paymentStatus";
import { ChatRoom } from "./components";
import { CardsEnumType } from "./data";
import styles from "./styles.module.scss";

const RecordDetail = () => {
  const { t } = useTranslation();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const query = new URLSearchParams(window.location.search);
  const pathname = window.location.pathname.split("/");
  const orderNumber = [...pathname].pop();
  const { TextArea } = Input;
  const [form] = Form.useForm();
  const [hasSubmitFeedback, setHasSubmitFeedback] = useState<boolean>(false);
  const { toServerTimezone, toLocalTimezone, formatDate, forceDate } =
    useGetTimezone();
  const [showFeedback, setShowFeedback] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<OrderDetailResponse>();

  const hideCompanionInput = [orderStatus.SUCCESS, orderStatus.CANCEL];

  const { data, refetch } = useGetOrderDetail(
    { order_number: orderNumber },
    { enabled: !query.get("feedback") }
  );

  const { data: paymentList, error: errorPayment } = useGetPaymentMethod({});
  const [selectedPayment, setSelectedPayment] = useState<string | undefined>(
    undefined
  );
  const { data: feedBackData, error: errorFeedback } = useGetOrderFeedbackNew(
    { order_number: orderNumber, order_token: query.get("order_token") || "" },
    { enabled: !!query.get("feedback") }
  );
  const { mutate: feedbackCreate, isPending: isPendingFeedbackCreate } =
    useOrderFeedbackCreate();
  const { mutateAsync: updateCompanion, isPending: isPendingUpdateCompanion } =
    useUpdateCompanion();

  const statusEnum: Record<string, { color: string }> = {
    [orderStatus.NOT_PAY]: {
      color: "var(--warn)",
    },
    [orderStatus.PAY_PROCESS]: {
      color: "var(--warn)",
    },
    [orderStatus.NOT_TEACHING]: {
      color: "var(--success)",
    },
    [orderStatus.REFUND_APPLY]: {
      color: "var(--warn)",
    },
    [orderStatus.SUCCESS]: {
      color: "var(--success)",
    },
    [orderStatus.CANCEL]: {
      color: "var(--empty)",
    },
    [orderStatus.REFUND]: {
      color: "var(--info)",
    },
    [orderStatus.REFUND_REJECT]: {
      color: "var(--error)",
    },
    [orderStatus.PAY_ERROR]: {
      color: "var(--error)",
    },
    [orderStatus.REFUND_ERROR]: {
      color: "var(--error)",
    },
    [orderStatus.DELETE]: {
      color: "var(--error)",
    },
  };

  type DescriptionItemWithVisible = NonNullable<
    DescriptionsProps["items"]
  >[number] & { hidden?: boolean };

  const orderInfoItems: DescriptionItemWithVisible[] = [
    {
      key: "1",
      label: t("recordDetail.orderNumber"),
      children: dataSource?.order_number ? (
        <Flex align="center" gap={16}>
          {`#${dataSource?.order_number}`}
          <Copy
            style={{ color: "var(--primary)", cursor: "pointer" }}
            onClick={() =>
              copyText(
                dataSource?.order_number || "",
                t("message.copy.success")
              )
            }
          />
        </Flex>
      ) : (
        "-"
      ),
    },
    {
      key: "2",
      label: t("recordDetail.sessionDate"),
      children: dataSource?.start_date
        ? `${formatDate(
            toLocalTimezone(dataSource?.start_date)
          )} - ${toLocalTimezone(dataSource?.end_date)?.format(
            `MM${t("normal.month")}DD${t("normal.date")}`
          )}`
        : "-",
      hidden: dataSource?.order_type === orderType.REPLENISH_ORDER,
    },
    {
      key: "3",
      label: t("recordDetail.instructorFee"),
      children: dataSource?.instructor_fee
        ? `${t(`currency.${process.env.REACT_APP_CURRENCY}`)} ${moneyFormat(
            dataSource?.instructor_fee
          )}`
        : "-",
      hidden: dataSource?.order_type === orderType.REPLENISH_ORDER,
    },
    {
      key: "4",
      label: t("recordDetail.orderPrice"),
      children: dataSource?.total_price
        ? `${t(`currency.${process.env.REACT_APP_CURRENCY}`)} ${moneyFormat(
            dataSource?.total_price
          )}`
        : "-",
    },
    {
      key: "5",
      label: t("checkout.paymentmethod.title"),
      children: selectedPayment || "-",
      hidden: selectedPayment === undefined,
    },
    {
      key: "6",
      label: t("recordDetail.orderDate"),
      children: dataSource?.booking_at
        ? toLocalTimezone(dataSource?.booking_at)?.format("YYYY-MM-DD HH:mm")
        : "-",
    },
    {
      key: "7",
      label: t("refund.paymentNumber"),
      children: dataSource?.order_payment_number ? (
        <Flex align="center" gap={16}>
          {`#${dataSource?.order_payment_number}`}
          <Copy
            style={{ color: "var(--primary)", cursor: "pointer" }}
            onClick={() =>
              copyText(
                dataSource?.order_payment_number || "",
                t("message.copy.success")
              )
            }
          />
        </Flex>
      ) : (
        "-"
      ),
    },
    {
      key: "8",
      label: (
        <div style={{ color: "var(--warn, #E07121)" }}>
          {t("record.refund.id")}
        </div>
      ),
      children: dataSource?.refund_number ? dataSource?.refund_number : "-",
      hidden: [
        paymentStatus.REFUND,
        paymentStatus.PARTIAL_REFUND,
        paymentStatus.REFUND_PROCESS,
      ].includes(dataSource?.payment_status || 0),
    },
  ];

  const ordererItems: DescriptionsProps["items"] = [
    {
      key: "1",
      label: t("recordDetail.nickname"),
      children: dataSource?.orderer?.name || "-",
    },
    {
      key: "2",
      label: t("recordDetail.email"),
      children: dataSource?.orderer?.email || "-",
    },
    {
      key: "3",
      label: dataSource?.orderer?.contact
        ? dataSource?.orderer.contact.length > 0 &&
          t(`normal.contact.${dataSource?.orderer?.contact[0].contact_type}`)
        : t("profile.contact.title"),
      children: dataSource?.orderer?.contact
        ? dataSource?.orderer?.contact.length > 0 &&
          dataSource?.orderer?.contact[0].contact_value
        : "-",
    },
    {
      key: "4",
      label: t("checkout.phone.title"),
      children:
        dataSource?.orderer?.area_code && dataSource?.orderer?.mobile
          ? `+${dataSource?.orderer.area_code}-${dataSource?.orderer?.mobile}`
          : "-",
    },
  ];

  const partnerItems = (partner: CompanionType): DescriptionsProps["items"] => [
    {
      key: "1",
      label: t("recordDetail.name"),
      children: `${partner.first_name} ${partner.last_name}`,
    },
    {
      key: "2",
      label: t("recordDetail.nickname"),
      children: partner.nickname,
    },
    {
      key: "3",
      label: t("checkout.data.age.title"),
      children: partner.age,
    },
    {
      key: "4",
      label: t("recordDetail.skiExp"),
      children: t(`userlevel.${partner.skill_level_code}`),
    },
  ];

  const orderContentItems: DescriptionItemWithVisible[] = [
    {
      key: "1",
      label: t("normal.skill.title"),
      children: t(`normal.skill.${dataSource?.skill_code}`),
    },
    {
      key: "2",
      label: t("normal.count.title"),
      children: (
        <>
          <div>{t("recordDetail.adults", { count: dataSource?.adults })}</div>
          <div>{t("recordDetail.kids", { count: dataSource?.kids })}</div>
        </>
      ),
    },
    {
      key: "3",
      label: t("recordDetail.skiResort"),
      children: t(`skiresort.${dataSource?.ski_resort_code}`),
      hidden: !dataSource?.coach_nickname,
    },
  ];

  const handleSubmitFeedback = () => {
    const value: {
      rating: number;
      description: string;
    } = form.getFieldsValue();
    // console.debug("value", value);
    feedbackCreate(
      {
        params: {
          ...value,
          order_number: orderNumber,
          order_token: query.get("order_token") || "",
        },
      },
      {
        onSuccess: (success: any) => {
          CustomMessage({ alertMessage: t("normal.submit.success") });
          setTimeout(() => (window.location.href = "/"), 1000);
        },
        onError: (error: any) => {
          console.debug("error", error);
        },
      }
    );
  };

  const renderPartnerDate = (): CardsEnumType[] => {
    if (dataSource?.companion && dataSource?.companion?.length > 0) {
      return [
        {
          title: t(`recordDetail.partnerData`),
          content: (
            <Flex vertical gap={12}>
              {Array.from({ length: dataSource?.companion.length || 0 }).map(
                (_, index) => (
                  <Flex vertical gap={16} key={index}>
                    <P2 weight="medium" color=" var(--primary-60)">
                      {t(`checkout.data.student.${index + 1}`)}
                    </P2>
                    <Descriptions
                      items={partnerItems(dataSource?.companion[index])}
                      column={screens.md ? 2 : 1}
                    />
                  </Flex>
                )
              )}
            </Flex>
          ),
        },
      ];
    } else {
      if (!hideCompanionInput.includes(dataSource?.status || 0)) {
        return Array.from({ length: dataSource?.total_people || 0 }).map(
          (_, index) => ({
            title: t(`checkout.data.student.${index + 1}`),
            content: (
              <CompanionForm index={index} skillCode={dataSource?.skill_code} />
            ),
          })
        );
      }
    }
    return [];
  };

  const convertToArray = (input: any) => {
    const result: any = {
      companion: [],
    };

    let index = 0;
    while (`first_name_${index}` in input) {
      result.companion.push({
        first_name: input[`first_name_${index}`],
        last_name: input[`last_name_${index}`],
        nickname: input[`nickname_${index}`],
        skill_level_code: input[`skill_level_code${index}`],
        age: input[`age_${index}`],
      });
      index++;
    }

    return result;
  };

  const handleUpdateCompanion = async (value: any) => {
    const request = {
      company_code: process.env.REACT_APP_COMPANY_CODE,
      order_number: orderNumber,
      ...convertToArray(value),
    };
    console.debug("request", request);
    await updateCompanion(
      { params: request },
      {
        onSuccess: async (data: any) => {
          CustomMessage({ alertMessage: t("normal.submit.success") });
          refetch();
        },
        onError: (error: any) => {
          CustomMessage({
            alertMessage: t([
              `error.${error.data.result.code}`,
              error.data.result.message,
            ]),
            alertType: "error",
          });
        },
      }
    );
  };

  const renderSessionTime = (session: SessionListType) => {
    let result = "";
    switch (session.session_type) {
      case sessionType.ALL_DAY:
        result += t("search.fullday");
        break;
      case sessionType.MORNING:
        result += t("search.morning");
        break;
      case sessionType.AFTERNOON:
        result += t("search.afternoon");
        break;
      default:
        result += t("search.fullday");
    }
    result += ` ${session.class_start_at}~${session.class_end_at}`;
    return result;
  };

  const cardsEnum: CardsEnumType[] = [
    {
      title: t(`recordDetail.feedback.title`),
      content: (
        <Form
          style={{ width: "100%" }}
          autoComplete={"off"}
          form={form}
          requiredMark={false}
          layout={"vertical"}
          clearOnDestroy
        >
          <P2 color="var(--Neutral-dark-70)">
            {t("recordDetail.feedback.content")}
          </P2>
          <Form.Item
            style={{ marginBottom: 0 }}
            name={"rating"}
            initialValue={1}
          >
            <Rate
              // value={star}
              // onChange={setStar}
              style={{ color: "#ff9900", fontSize: 32, margin: "16px 0 32px" }}
              allowClear={false}
            />
          </Form.Item>
          <Form.Item
            name={"description"}
            label={
              <P2 color="var(--Neutral-dark-70)">
                {t("recordDetail.feedback.textArea")}
              </P2>
            }
            initialValue={""}
          >
            <TextArea
              maxLength={300}
              showCount
              rows={5}
              placeholder={t("recordDetail.feedback.textArea.placeholder")}
            />
          </Form.Item>
          <Form.Item
            label={null}
            style={{ textAlign: "right", marginTop: screens.sm ? 52 : 45 }}
          >
            <Button
              type="primary"
              style={{
                width: screens.sm ? "auto" : "100%",
                height: screens.sm ? 48 : 36,
              }}
              onClick={() => handleSubmitFeedback()}
              loading={isPendingFeedbackCreate}
            >
              {t("normal.submit")}
            </Button>
          </Form.Item>
        </Form>
      ),
      hide: !showFeedback,
    },
    {
      title: t(`recordDetail.orderInfo`),
      content: (
        <Descriptions
          items={orderInfoItems.filter(
            (_order: DescriptionItemWithVisible) => !_order.hidden
          )}
          column={screens.md ? 2 : 1}
        />
      ),
      extra: (
        <P1 weight="medium" color={statusEnum[dataSource?.status || 1].color}>
          {dataSource?.status
            ? t(`recordDetail.status.${dataSource?.status}`)
            : "-"}
        </P1>
      ),
    },
    {
      title: t(`recordDetail.orderContent`),
      content: (
        <>
          <H6 weight="medium" color="var(--text-content-1)">
            {dataSource?.coach_nickname
              ? t("normal.coach.nickname", {
                  nickname: dataSource?.coach_nickname,
                })
              : t(`skiresort.${dataSource?.ski_resort_code}`)}
          </H6>
          <Descriptions
            items={orderContentItems.filter(
              (item: DescriptionItemWithVisible) => !item.hidden
            )}
            column={dataSource?.coach_nickname ? 3 : 2}
            layout="vertical"
            style={{ margin: "16px 0" }}
          />
          {dataSource?.session_list.map(
            (session: SessionListType, index: number) => (
              <>
                <div className={styles.sessionContainer} key={index}>
                  <Flex align="center" gap={8} style={{ marginBottom: 12 }}>
                    <CalendarBlankFilled
                      style={{ color: "var(--primary)", fontSize: 20 }}
                    />
                    <P1 weight="medium" color="var(--primary)">
                      {formatDate(toLocalTimezone(session.date))}
                    </P1>
                  </Flex>
                  <Flex gap={12}>
                    {session.session_img ? (
                      <img
                        src={session.session_img}
                        alt="ski_img"
                        className={styles.image}
                      />
                    ) : (
                      <img
                        src={defaultImage}
                        alt="ski_img"
                        className={styles.image}
                      />
                    )}
                    <div>
                      <P2
                        color="var(--text-content-1)"
                        style={{ marginBottom: 8 }}
                      >
                        {session.session_name}
                      </P2>
                      <Flex align="center" gap={8}>
                        <ClockFilled
                          style={{
                            color: "var(--text-content-2)",
                            fontSize: 14,
                          }}
                        />
                        {/* TODO: not full day */}
                        <P2 color="var(--text-content-1)">
                          {renderSessionTime(session)}
                        </P2>
                      </Flex>
                    </div>
                    <P2
                      color="var(--text-content-2)"
                      className={styles.totalPrice}
                    >
                      {`${t(
                        `currency.${process.env.REACT_APP_CURRENCY}`
                      )} ${moneyFormat(session.price + session.extra_charge)}`}
                    </P2>
                  </Flex>
                </div>
                <Divider style={{ margin: "18px 0" }} />
              </>
            )
          )}
          <Flex vertical align="flex-end" gap={16}>
            <Flex align="center" justify="space-between">
              <P2 color="var(--text-content-2)">{t("search.summary")}</P2>
              <H6 color="var(--primary)" weight="heavy">
                {`${t(
                  `currency.${process.env.REACT_APP_CURRENCY}`
                )} ${moneyFormat(dataSource?.total_price)}`}
              </H6>
            </Flex>
            {dataSource?.status &&
            [paymentStatus.PARTIAL_REFUND, paymentStatus.REFUND].includes(
              dataSource?.payment_status
            ) ? (
              <Flex
                gap={20}
                align="center"
                justify={screens.sm ? "" : "space-between"}
                style={{ width: screens.sm ? "auto" : "100%" }}
              >
                <P2 color="var(--text-content-2)">
                  {t("record.alreadyrefund")}
                </P2>
                <P2 color="var(--warn)" weight="medium">
                  {`${t(
                    `currency.${process.env.REACT_APP_CURRENCY}`
                  )} ${moneyFormat(dataSource?.refund_amount)}`}
                </P2>
              </Flex>
            ) : null}
          </Flex>
        </>
      ),
      hide: dataSource?.order_type === orderType.REPLENISH_ORDER,
    },
    {
      title: t(`recordDetail.orderer`),
      content: (
        <Descriptions items={ordererItems} column={screens.md ? 2 : 1} />
      ),
      hide: dataSource?.order_type === orderType.REPLENISH_ORDER,
    },
    ...renderPartnerDate(),
    {
      title: t(`recordDetail.remark`),
      content: dataSource?.remark,
      hide: dataSource?.order_type === orderType.REPLENISH_ORDER,
    },
    {
      title: t(`order.fixreason`),
      content: dataSource?.fix_reason,
      hide: dataSource?.order_type === orderType.GENERAL_ORDER,
    },
  ];

  useEffect(() => {
    if (!errorFeedback) return;
    // Safely access custom error properties
    const errorCode =
      (errorFeedback &&
        typeof errorFeedback === "object" &&
        "data" in errorFeedback &&
        (errorFeedback as any).data?.result?.code) ||
      undefined;
    setHasSubmitFeedback(errorCode === "order.HAS_BEEN_FEEDBACK");

    console.debug("errorFeedback", errorFeedback);
  }, [errorFeedback]);

  useEffect(() => {
    if (!paymentList || !data) return;
    const _selectedPayment: PaymentResponse | undefined = paymentList.find(
      (_payment: PaymentResponse) =>
        _payment.payment_method_code === data.payment_method_code
    );
    if (_selectedPayment) {
      setSelectedPayment(_selectedPayment.name);
    }
  }, [paymentList, data]);

  useEffect(() => {
    if (data) console.debug("orderDetail", data);
  }, [data]);

  useEffect(() => {
    if (feedBackData) console.debug("feedBackData", feedBackData);
  }, [feedBackData]);

  useEffect(() => {
    if (query.get("feedback")) {
      setShowFeedback(JSON.parse(query.get("feedback") || ""));
      setDataSource(feedBackData);
    } else {
      setDataSource(data);
    }
  }, [data, feedBackData]);

  return (
    <ContentWrapper>
      {hasSubmitFeedback ? (
        <div
          className={`${styles.emptyFeedbackWrapper} ${
            screens.xs ? styles.mobile : ""
          }`}
        >
          <Image
            src={FeedbackSubmitedIcon}
            width={112}
            height={112}
            preview={false}
          />
          <P2 weight="medium" color="#1C222B">
            {t("feedback.empty.title")}
          </P2>
          <P3 color="#5E718D">{t("feedback.empty.subtitle")}</P3>
        </div>
      ) : (
        <div className={`${styles.recordDetail} recordDetail`}>
          <Form
            autoComplete={"off"}
            onFinish={handleUpdateCompanion}
            // form={form}
            // requiredMark={false}
            layout={"vertical"}
            style={{ gap: screens.sm ? 24 : 16 }}
            className={styles.form}
          >
            {cardsEnum.map((card: CardsEnumType, index: number) => {
              if (card.hide) return null;
              return (
                <Card
                  key={index}
                  style={{ width: "100%" }}
                  size={screens.sm ? "default" : "small"}
                  title={
                    <P1 weight="medium" color=" var(--text-content-1)">
                      {card.title}
                    </P1>
                  }
                  extra={card.extra}
                  variant="borderless"
                >
                  {card.content}
                </Card>
              );
            })}
            {dataSource?.companion &&
              dataSource?.companion?.length <= 0 &&
              !hideCompanionInput.includes(dataSource?.status || 0) && (
                <Flex justify="center">
                  <Button
                    loading={isPendingUpdateCompanion}
                    type="primary"
                    htmlType="submit"
                    style={{ width: "20%" }}
                  >
                    {t("normal.save")}
                  </Button>
                </Flex>
              )}
          </Form>
          {!showFeedback &&
          dataSource?.order_type === orderType.GENERAL_ORDER ? (
            <ChatRoom transactionID={dataSource?.order_number} />
          ) : null}
        </div>
      )}
    </ContentWrapper>
  );
};

export default RecordDetail;
